import { stripMarkdownFormatting } from 'lib/string/stripMarkdown'
import { isManualPackageValidityText } from 'components/OfferPage/Accommodation/packageInclusionValidityUtils'

/** Display category used by the refreshed modal to choose its room and inclusion sections. */
type StayUpgradeType = 'packageOnly' | 'roomOnly' | 'roomAndPackage'

/** An upgraded-package inclusion paired with whether it is absent from the selected base package. */
interface StayUpgradeInclusion {
  inclusion: App.PackageInclusion
  isNew: boolean
}

/** Ordered upgraded-package inclusions and counts used by the refreshed modal. */
interface StayUpgradeInclusionsView {
  items: Array<StayUpgradeInclusion>
  newCount: number
  totalCount: number
}

/**
 * Normalises inclusion copy for reliable comparison by removing list and
 * Markdown formatting, folding whitespace, and ignoring letter case.
 */
function normaliseStayUpgradeInclusionText(text: string): string {
  return stripMarkdownFormatting(text.trim().replace(/^-\s+/, '')).replace(/\s+/g, ' ').toLowerCase()
}

/**
 * Compares the suggested package inclusions with the selected package.
 * New rows are ordered first while preserving source order within each group.
 */
export function getStayUpgradeInclusions(
  baseInclusions: Array<App.PackageInclusion>,
  upgradeInclusions: Array<App.PackageInclusion>,
): StayUpgradeInclusionsView {
  const baseInclusionTexts = new Set(
    baseInclusions.map(inclusion => normaliseStayUpgradeInclusionText(inclusion.description)),
  )
  const classifiedInclusions = upgradeInclusions
    .map(inclusion => {
      const isPackageValidityText = isManualPackageValidityText(inclusion.description)

      return {
        inclusion,
        isNew: !isPackageValidityText && !baseInclusionTexts.has(normaliseStayUpgradeInclusionText(inclusion.description)),
      }
    })
    .filter(item => item.inclusion.description.trim().length > 0)
  const orderedItems = [
    ...classifiedInclusions.filter(item => item.isNew),
    ...classifiedInclusions.filter(item => !item.isNew),
  ]
  const countableItems = orderedItems.filter(item => !isManualPackageValidityText(item.inclusion.description))

  return {
    items: orderedItems,
    newCount: countableItems.filter(item => item.isNew).length,
    totalCount: countableItems.length,
  }
}

/**
 * Returns whether the selected and suggested packages refer to the same room.
 * Shared by the control title and refreshed modal classification so both arms
 * use the control modal's established room comparison.
 */
export function isSameStayUpgradeRoomType(
  basePackage: App.Package,
  upgradePackage: App.Package,
): boolean {
  return !!basePackage.roomType?.id && basePackage.roomType.id === upgradePackage.roomType?.id
}

/**
 * Classifies the visual upgrade content from room identity, rate plan identity,
 * and the inclusion diff. This does not select the upgrade candidate; both
 * experiment arms continue to use the control checkout selection logic.
 */
export function getStayUpgradeType(
  basePackage: App.Package,
  upgradePackage: App.Package,
  inclusionsView: StayUpgradeInclusionsView,
): StayUpgradeType {
  if (isSameStayUpgradeRoomType(basePackage, upgradePackage)) return 'packageOnly'

  const hasSameRatePlan = !!basePackage.roomRate?.ratePlanId &&
    basePackage.roomRate.ratePlanId === upgradePackage.roomRate?.ratePlanId

  if (hasSameRatePlan && inclusionsView.newCount === 0) return 'roomOnly'

  return 'roomAndPackage'
}

/** Stable identifier for a supported refreshed-modal highlight. */
export type StayUpgradeHighlightId =
  'private-pool' |
  'ocean-view' |
  'mountain-view' |
  'city-view' |
  'garden-view' |
  'balcony' |
  'jacuzzi' |
  'bathtub' |
  'living-area' |
  'kitchenette' |
  'all-inclusive' |
  'breakfast' |
  'kids-stay-free' |
  'lounge-access' |
  'early-check-in' |
  'late-checkout' |
  'room-service' |
  'transfers'

/** A highlight selected for display in the refreshed modal. */
interface StayUpgradeHighlight {
  id: StayUpgradeHighlightId
}

interface HighlightRule extends StayUpgradeHighlight {
  pattern: RegExp
}

/**
 * Highlight matching rules in display-priority order. Patterns deliberately
 * use word boundaries so only the ticket's exact, safe triggers qualify.
 */
const HIGHLIGHT_RULES: Array<HighlightRule> = [
  { id: 'private-pool', pattern: /\bprivate pool\b/i },
  { id: 'ocean-view', pattern: /\bocean views?\b/i },
  { id: 'mountain-view', pattern: /\bmountain views?\b/i },
  { id: 'city-view', pattern: /\bcity views?\b/i },
  { id: 'garden-view', pattern: /\bgarden views?\b/i },
  { id: 'balcony', pattern: /\bprivate balcony\b/i },
  { id: 'jacuzzi', pattern: /\bjacuzzi\b/i },
  { id: 'bathtub', pattern: /\b(?:bathtub|soaking tub|freestanding bath|spa bath)\b/i },
  { id: 'living-area', pattern: /\b(?:separate living area|separate living room)\b/i },
  { id: 'kitchenette', pattern: /\bkitchenette\b/i },
  { id: 'all-inclusive', pattern: /\ball(?:-| )inclusive\b/i },
  { id: 'breakfast', pattern: /\bdaily breakfast\b/i },
  { id: 'kids-stay-free', pattern: /\b(?:kids stay free|children stay free)\b/i },
  { id: 'lounge-access', pattern: /\blounge access\b/i },
  { id: 'early-check-in', pattern: /\bearly check-in\b/i },
  { id: 'late-checkout', pattern: /\blate checkout\b/i },
  { id: 'room-service', pattern: /\b(?:room service|in-room dining)\b/i },
  { id: 'transfers', pattern: /\bairport transfers?\b/i },
]

function getHighlightTexts(pkg: App.Package, inclusions: Array<App.PackageInclusion>): Array<string> {
  return [
    pkg.roomType?.name,
    pkg.roomType?.description,
    ...(pkg.roomType?.amenities.map(amenity => amenity.description) ?? []),
    ...inclusions.map(inclusion => inclusion.description),
  ].filter((text): text is string => text !== undefined && text.trim().length > 0)
    .map(normaliseStayUpgradeInclusionText)
}

/**
 * Selects up to four highlights from room copy, amenities, and inclusions.
 * Newly gained highlights are ordered before highlights shared with the base
 * package, with ties resolved by the display-priority order above.
 */
export function getStayUpgradeHighlights(
  basePackage: App.Package,
  upgradePackage: App.Package,
  baseInclusions: Array<App.PackageInclusion>,
  upgradeInclusions: Array<App.PackageInclusion>,
): Array<StayUpgradeHighlight> {
  const baseTexts = getHighlightTexts(basePackage, baseInclusions)
  const upgradeTexts = getHighlightTexts(upgradePackage, upgradeInclusions)
  const matchedRules = HIGHLIGHT_RULES.filter(rule => upgradeTexts.some(text => rule.pattern.test(text)))
  const gainedRules = matchedRules.filter(rule => !baseTexts.some(text => rule.pattern.test(text)))
  const sharedRules = matchedRules.filter(rule => !gainedRules.includes(rule))

  return [...gainedRules, ...sharedRules].slice(0, 4).map(rule => ({ id: rule.id }))
}
